import React from 'react';
import EngineCleanupBase from '../../services/engines/EngineCleanupBase';
import jxWindowInstanceCache from './JxWindowInstanceCache';
import jxWindowCache from './JxWindowCache';
import { JxWindowEvents } from './JxWindowEvents';
import { WindowDisplayStates } from './Types/WindowDisplayStates';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import { publish } from '../../services/utils/pubsub';

const _name = 'JxWindowManager';

/**
 * Manages JxForm window instances — open, close, component registry, painter.
 * Separate from JxWindowEngine which handles pos/size/zIndex only.
 * clear() fires on onClearCache (logout) — clears all instances and triggers repaint.
 * Singleton — shared across the app.
 *
 * No direct import of tabEngine — that dragged the whole TabEngine/tabDefinitions/eager-tabs
 * tree into every JxForm subclass's module graph (via JxFormBase → JxWindowManager → tabEngine),
 * causing a circular-import TDZ whenever a JxForm dialog was also reachable from that tree
 * (e.g. CreateNoteDlgWidget via DevTab/ComponentTestTab). Active tab id is now tracked locally,
 * kept in sync via ComponentNotifier('TabEngine', 'tabChanged') instead.
 */
class JxWindowManager extends EngineCleanupBase {
    _components = {}; // name → React component
    _builders = {}; // name → optionBuilder component (optional)
    _painter = null;
    _booted = false;
    _activeTabId = null; // synced via TabEngine 'tabChanged' notify, see _handleTabChanged
    _unsubscribeTabChanged = null;

    // pending state while an optionBuilder dialog is open
    // ownerTabId is captured at openWindow time — frozen so tab switches during builder don't affect it
    _pendingOpen = null; // { name, ownerTabId } — window waiting for builder to confirm
    _builderOpen = false;

    // id → { title, name } — registered by JxMinimizableFormBase.componentDidMount, independent
    // of jxWindowInstanceCache since not every minimizable widget is opened via openWindow()
    // (e.g. CommandBarWidget is mounted directly by YourLibraryTabPainter).
    _minimizables = {};

    bootup = () => {
        if (this._booted) return;
        this._booted = true;

        this.initialize(_name);
        this._unsubscribeTabChanged = ComponentNotifier.subscribe('TabEngine', this._handleTabChanged);
    };

    /**
     * Keeps _activeTabId in sync with TabEngine, without importing TabEngine directly.
     * @param {{name: string, target: string, data: object}} e
     */
    _handleTabChanged = (e) => {
        if (e.target !== 'tabChanged') return;
        this._activeTabId = e.data?.id ?? null;
    };

    /**
     * Registers a React component class by name, with an optional option builder dialog.
     * If an optionBuilder is provided, openWindow shows it first before opening the window.
     * @param {string} name
     * @param {React.ComponentType} component
     * @param {React.ComponentType|null} optionBuilder
     */
    registerComponent = (name, component, optionBuilder = null) => {
        this._components[name] = component;
        if (optionBuilder) this._builders[name] = optionBuilder;
    };

    /**
     * Called by PageContent every render to keep repaint reference fresh.
     * @param {function} repaint
     */
    registerPainter = (repaint) => {
        this._painter = repaint;
    };

    /**
     * Opens a new window instance.
     * If an optionBuilder is registered for this name, shows it first — unless skipBuilder is true,
     * in which case the builder is bypassed and the window opens directly with the provided data.
     * ownerTabId is captured now — frozen for the duration of the builder session.
     * @param {string} name - component type string (e.g. 'JxClockTestForm')
     * @param {string} owner - 'global' | tab id (e.g. 'library', 'dev')
     * @param {object} data - props passed to the component
     * @param {boolean} skipBuilder - if true, bypass optionBuilder even when registered
     * @returns {string|null} assigned id, or null when builder is shown first
     */
    openWindow = (name, owner, data = {}, skipBuilder = false) => {
        // open optionBuilder dialog when needed
        if (this._builders[name] && !skipBuilder) {
            // Capture active tab at open time — not at confirm time
            const ownerTabId = this._activeTabId;
            this._pendingOpen = { name, ownerTabId };
            this._builderOpen = true;
            this._painter?.();

            return null;
        }

        // create the actual window
        return this._createWindow(name, owner, data);
    };

    /**
     * Closes a window instance by id. Triggers repaint.
     * @param {string} id
     */
    closeWindow = (id) => {
        jxWindowInstanceCache.removeWindow(id);
        this._painter?.();
    };

    /**
     * Minimizes a window — instance stays registered (unlike closeWindow), only displayState
     * flips to Minimized via WindowGeoObj.minimize(). Hiding the actual window is NOT handled
     * here — JxMinimizableFormBase.isMinimized (read from jxWindowCache) is what each widget's
     * own render() combines with props.open, since not every minimizable widget goes through
     * paint() (e.g. CommandBarWidget is mounted directly by YourLibraryTabPainter).
     * targetAnchor is null for now — TaskbarWidget doesn't animate to a dock slot yet.
     * @param {string} id - windowId (this.getId() of the JxForm instance)
     */
    minimizeWindow = (id) => {
        const geo = jxWindowCache.getByWindowId(id) ?? jxWindowCache.setByWindowId(id, {});
        geo.minimize(null);
        jxWindowCache.setByWindowId(id, {
            displayState: geo.displayState,
            restoreGeo: geo.restoreGeo,
            targetAnchor: geo.targetAnchor,
        });
        this._painter?.();
        ComponentNotifier.notify(_name, 'windowsChanged', { id });
    };

    /**
     * Restores a minimized window — flips displayState back to Normal, restores pos/size
     * from restoreGeo, and brings it to the front + active (same OnWindowSelected path as
     * a regular click, see JxFormBase._applyZIndex). No-op if window was never cached.
     * @param {string} id - windowId
     */
    restoreWindow = (id) => {
        const geo = jxWindowCache.getByWindowId(id);
        if (!geo) return;

        geo.restore();
        jxWindowCache.setByWindowId(id, {
            posX: geo.posX,
            posY: geo.posY,
            width: geo.width,
            height: geo.height,
            displayState: geo.displayState,
            restoreGeo: geo.restoreGeo,
            targetAnchor: geo.targetAnchor,
        });
        this._painter?.();

        publish(JxWindowEvents.channel, { name: JxWindowEvents.onWindowSelected, target: id, data: null });
        ComponentNotifier.notify(_name, 'windowsChanged', { id });
    };

    /**
     * Brings a window to the front no matter its state: minimized = restore, else select.
     * Fires 'windowSummoned' so the window can focus its input (JxMinimizableFormBase.onSummoned).
     * @param {string} id - windowId
     */
    summonWindow = (id) => {
        if (jxWindowCache.getByWindowId(id)?.displayState === WindowDisplayStates.Minimized) {
            this.restoreWindow(id);
        } else {
            publish(JxWindowEvents.channel, { name: JxWindowEvents.onWindowSelected, target: id, data: null });
        }
        ComponentNotifier.notify(_name, 'windowSummoned', { id });
    };

    /**
     * Registers a minimizable widget instance for the Taskbar — called from
     * JxMinimizableFormBase.componentDidMount. Not tied to openWindow/jxWindowInstanceCache.
     * @param {string} id - windowId (this.getId())
     * @param {{title: string, pinned?: boolean}} meta - pinned = always shown left in the Taskbar
     */
    registerMinimizable = (id, meta) => {
        this._minimizables[id] = meta;
        ComponentNotifier.notify(_name, 'windowsChanged', { id });
    };

    /**
     * Unregisters a minimizable widget instance — called from JxMinimizableFormBase.componentWillUnmount.
     * @param {string} id
     */
    unregisterMinimizable = (id) => {
        delete this._minimizables[id];
        ComponentNotifier.notify(_name, 'windowsChanged', { id });
    };

    /**
     * Returns Taskbar entries — registered minimizables currently in Minimized displayState.
     * @returns {{id: string, title: string}[]}
     */
    getMinimizedEntries = () =>
        Object.entries(this._minimizables)
            .filter(([id, meta]) => !meta.pinned && jxWindowCache.getByWindowId(id)?.displayState === WindowDisplayStates.Minimized)
            .map(([id, meta]) => ({ id, ...meta }));

    /**
     * Taskbar entries that are always shown (left), minimized or not.
     * @returns {{id: string, title: string, isMinimized: boolean}[]}
     */
    getPinnedEntries = () =>
        Object.entries(this._minimizables)
            .filter(([, meta]) => meta.pinned)
            .map(([id, meta]) => ({
                id,
                ...meta,
                isMinimized: jxWindowCache.getByWindowId(id)?.displayState === WindowDisplayStates.Minimized,
            }));

    /**
     * Returns all open window instances.
     * @returns {object[]}
     */
    getAllWindows = () => jxWindowInstanceCache.getAll();

    /**
     * Patches props on an existing window instance and triggers repaint.
     * Works for any registered window (FloatingContentPanel, CalendarCard, Clock, etc.)
     * Also syncs patch.title into _minimizables if this id is registered there — otherwise a
     * Taskbar entry keeps showing the title captured at mount time even after setTitle().
     * @param {string} id - window instance id (e.g. 'FloatingContentPanel_01')
     * @param {object} patch - partial props to merge, e.g. { title: 'Query: xxx' }
     */
    updateWindowProps = (id, patch) => {
        const inst = jxWindowInstanceCache.getAll().find((w) => w.rk === id);
        if (!inst) return;
        inst.props = { ...inst.props, ...patch };

        if (this._minimizables[id] && patch.title !== undefined) {
            this._minimizables[id] = { ...this._minimizables[id], title: patch.title };
        }

        this._painter?.();
    };

    /**
     * Returns registered React component class by name, or null if not registered.
     * @param {string} name
     * @returns {React.ComponentType|null}
     */
    getComponent = (name) => this._components[name] ?? null;

    /**
     * Renders all open window instances + active optionBuilder if pending.
     * Called directly from PageContent render.
     * owner === 'global' → always open.
     * owner === tab id → open only when that tab is active.
     * @returns {React.ReactElement[]}
     */
    paint = () => {
        const activeTabId = this._activeTabId;

        const windows = jxWindowInstanceCache.getAll().map((inst) => {
            const Component = this._components[inst.name];
            if (!Component) return null;
            const open = inst.owner === 'global' || inst.owner === activeTabId;
            return <Component key={inst.id} id={inst.id} {...inst.props} open={open} onClose={() => this.closeWindow(inst.id)} />;
        });

        // Render active optionBuilder on top if pending
        // currentTabId is the frozen ownerTabId — not the live activeTabId
        if (this._builderOpen && this._pendingOpen) {
            const Builder = this._builders[this._pendingOpen.name];
            if (Builder) {
                windows.push(
                    <Builder
                        key="__option_builder__"
                        open={true}
                        currentTabId={this._pendingOpen.ownerTabId}
                        onExit={this._handleBuilderExit}
                    />,
                );
            }
        }

        return windows;
    };

    clear() {
        super.clear();

        if (this._unsubscribeTabChanged) this._unsubscribeTabChanged = this._unsubscribeTabChanged();

        jxWindowInstanceCache.clear();
        this._pendingOpen = null;
        this._builderOpen = false;
        this._activeTabId = null;
        this._booted = false;
        this._painter?.();
    }

    // ─── private ─────────────────────────────────────────

    /**
     * Called by optionBuilder on confirm or cancel.
     * owner + data null = canceled — no window opened.
     * @param {string|null} owner
     * @param {object|null} data
     */
    _handleBuilderExit = (owner, data) => {
        this._builderOpen = false;
        const pending = this._pendingOpen;
        this._pendingOpen = null;

        if (!owner || !pending) {
            this._painter?.();
            return;
        }

        this._createWindow(pending.name, owner, data);
    };

    /**
     * Creates a window instance, adds to cache, triggers repaint.
     * Geo (cache vs default) is resolved by the window itself (JxFormBase.componentDidMount
     * calls jxWindowEngine.getPos(this.getId())) — no longer passed in as a prop here.
     * @param {string} name
     * @param {string} owner
     * @param {object} data
     * @returns {string} assigned id
     */
    _createWindow = (name, owner, data) => {
        const id = jxWindowInstanceCache.getNextId(name);
        jxWindowInstanceCache.addWindow({ id, name, props: data, owner });
        this._painter?.();
        return id;
    };
}

const jxWindowManager = new JxWindowManager();
export default jxWindowManager;
